import { FormControlLabel, Paper, Stack, Typography } from '@mui/material';
import { Box } from '@mui/system';
import QueryCard from '../QueryCard';
import { useDispatch, useSelector } from 'react-redux';
import {
    addConstruct,
    addInstrument,
    removeConstruct,
    removeInstrument,
    selectAgeRange,
    selectConstructs,
    selectInstruments,
    setAgeRange,
    setConstructs,
    setInstruments,
} from '../../../data-wrangler/dataWranglerSlice';
import { Session } from './SessionSelector';
import LassoCheckbox from '../../../../components/form/LassoCheckbox';
import { colours } from '../../../../StyleConstants';
import { Dispatch, SetStateAction } from 'react';
import { FilterFlow, HoverData, QueryType } from '../QueryWizard';

export type ResearchDomain ={
  name: string;
  sessions: string[];
  instruments: string[]
}

type Props = {
  sessions: Session[];
  researchDomains: ResearchDomain[];
  domainToInstrumentsMapping: any;
  hoverTarget: HoverData;
  setHoverTarget: Dispatch<SetStateAction<HoverData>>;
  filterFlow: FilterFlow;
  setFilterFlow: Dispatch<SetStateAction<FilterFlow>>;
}

const ResearchDomainSelector = ({ researchDomains,hoverTarget, setHoverTarget, domainToInstrumentsMapping, filterFlow, setFilterFlow } : Props) => {
    const dispatch = useDispatch();
    const selectedSessions = useSelector(selectAgeRange);
    const selectedInstruments = useSelector(selectInstruments);
    const selectedDomains = useSelector(selectConstructs);

    const onDomainClick = (domain: string, selectedDomains: string[]) => {
        if (selectedDomains?.includes(domain)) {
            removeInstrumentsForSelectedDomain(domain);
            dispatch(removeConstruct(domain));
            if(filterFlow === FilterFlow.DomainBasedFiltering && selectedDomains.length === 1){
                dispatch(setAgeRange([]));
                dispatch(setInstruments([]));
                setFilterFlow(FilterFlow.None);
            }
        } else {
            dispatch(addConstruct(domain));
        }
        switch(filterFlow) {
        case FilterFlow.SessionBasedFiltering: {
            return;
        }
        case FilterFlow.DomainBasedFiltering: {
            return;
        }
        case FilterFlow.InstrumentBasedFiltering: {
            return;
        }
        case FilterFlow.None: {
            setFilterFlow(FilterFlow.DomainBasedFiltering);
            return;
        }
        }
    }

    function removeInstrumentsForSelectedDomain(domain: string) {
        const selected = domainToInstrumentsMapping[domain];
        const instruments = new Set(Object.keys(selected));
        selectedInstruments.forEach((instrument: string) => {
            const isExclusiveToConstruct = !isInstrumentPresentInAnotherConstruct(domain, instrument);
            const shouldRemoveInstrument = instruments.has(instrument) && isExclusiveToConstruct
            if (shouldRemoveInstrument) {
                dispatch(removeInstrument(instrument));
            }
        });
    }

    function isInstrumentPresentInAnotherConstruct(construct: string, instrument: string) {
        const allSelectedConstructsInstruments = new Set<string>();
        for (const selectedConstruct of selectedDomains) {
            if (construct !== selectedConstruct) {
                const instruments = Object.keys(domainToInstrumentsMapping[selectedConstruct]);
                instruments.forEach((inst: string) => allSelectedConstructsInstruments.add(inst));
            }
        }
        return allSelectedConstructsInstruments.has(instrument);
    }

    const isChecked = (domainInstruments: string[]) => {
        const selectedInstrumentSet = new Set(selectedInstruments);
        for(const inst of domainInstruments) {
            if(!selectedInstrumentSet.has(inst)){
                return false;
            }
        }
        return true;
    }

    const onChecked = (checked: boolean, domain: ResearchDomain) => {
        if(!checked) {
            for(const inst of domain.instruments) {
                dispatch(removeInstrument(inst));
            }
        } else {
            const selectedDomainsSet = new Set([...selectedDomains, domain.name]);
            for(const inst of domain.instruments) {
                dispatch(addInstrument(inst));
            }
            dispatch(setAgeRange(Array.from(new Set([...selectedSessions, ...domain.sessions]))));
            dispatch(setConstructs(Array.from(selectedDomainsSet)));
        }
    }

    const handleMouseEnter = (domain: ResearchDomain) => {
        setHoverTarget({
            type: QueryType.RESEARCH_DOMAIN,
            data: {
                sessions: domain.sessions,
                researchDomains: [],
                instruments: domain.instruments,
            }
        });
    }

    const handleMouseLeave = () => {
        setHoverTarget({
            type: QueryType.NONE,
            data: {
                sessions: [],
                researchDomains: [],
                instruments: [],
            }
        });
    }

    const isHighlighted = (domain: ResearchDomain) => {
        if(hoverTarget.type === QueryType.SESSION || hoverTarget.type === QueryType.INSTRUMENT) {
            return hoverTarget.data.researchDomains.includes(domain.name)
        }else{
            return false;
        }
    }

    const domainInSessionSelection = (domain: ResearchDomain) => {
        if(!(hoverTarget.type === QueryType.NONE)) return false;
        const sessionsSet = new Set(selectedSessions);
        return domain.instruments.some((inst: string) => sessionsSet.has(inst));
    }

    const domainInInstrumentSelection = (domain: ResearchDomain) => {
        if(!(hoverTarget.type === QueryType.NONE)) return false;
        const instrumentsSet = new Set(selectedInstruments);
        return domain.instruments.some((inst: string) => instrumentsSet.has(inst));
    }

    const isDisabled = () => {
        switch(filterFlow) {
        case FilterFlow.SessionBasedFiltering:
            return true;
        case FilterFlow.DomainBasedFiltering:
            return false;
        case FilterFlow.InstrumentBasedFiltering:
            return true;
        case FilterFlow.None:
            return false;
        }
    }

    return (
        <Paper sx={{
            flexGrow: 1,
            padding: 3,
            height: 'calc(50% - 16px)',
            outline: selectedDomains?.length === 0 ? `1px solid ${colours.orange.orange80}` : "none"
        }}>
            <Stack height={"100%"} gap={2}>
                <Box>
                    <Typography variant={"h2"} fontSize={"18px"} fontWeight={"bold"}>Research domains <span style={{ fontSize: "12px", color: colours.success.success }}>(Optional)</span>
                    </Typography>
                    <Typography fontSize={"14px"}>Choose research domains to filter the instrument selection</Typography>
                </Box>
                <Box
                    display={"grid"}
                    gap={2}
                    gridAutoRows={"minmax(125px, max-content)"}
                    gridTemplateColumns={"repeat(auto-fit, minmax(100px, 1fr))"}
                    flex={"1 1 auto"}
                    overflow={"auto"}
                    minHeight={"0px"}
                    padding={2}
                >
                    {
                        researchDomains?.map((researchDomain) =>
                            <QueryCard
                                id={`query-wizard-domain-${researchDomain.name}`}
                                key={researchDomain.name}
                                label={researchDomain.name}
                                onClick={() => onDomainClick(researchDomain.name, selectedDomains)}
                                onMouseEnter={() => handleMouseEnter(researchDomain)}
                                onMouseLeave={handleMouseLeave}
                                selected={selectedDomains.includes(researchDomain.name)}
                                highlighted={
                                    !(hoverTarget.type === QueryType.RESEARCH_DOMAIN) &&
                  (isHighlighted(researchDomain)
                  || domainInSessionSelection(researchDomain)
                  || domainInInstrumentSelection(researchDomain))
                                }
                                disabled={isDisabled()}
                            >
                                <Stack justifyContent={"center"} alignItems={"center"} height={"100%"}>
                                    <Stack
                                        flexDirection={"row"}
                                        alignItems={"center"}
                                        gap={1}
                                        top={"4px"}
                                        right={"14px"}
                                        position={"absolute"}
                                    >
                                        <FormControlLabel
                                            sx={{
                                                ".MuiFormControlLabel-label": {
                                                    fontSize: "0.7rem",
                                                } }}
                                            control={
                                                <LassoCheckbox
                                                    id={`query-wizard-${researchDomain.name}-select-all-checkbox`}
                                                    sx={{ padding: 0, marginLeft: 1 }}
                                                    size={"small"}
                                                    checked={isChecked(researchDomain.instruments)}
                                                    onChange={(e, checked) => {
                                                        onChecked(checked, researchDomain);
                                                    }}
                                                    disabled={isDisabled()}
                                                />}
                                            label="Select All"
                                            aria-label={`Select All ${researchDomain.name} instruments`}
                                            labelPlacement={"start"}
                                        />
                                    </Stack>
                                    <Typography
                                        marginLeft={0}
                                        fontSize={"12px"}
                                        fontWeight={"bold"}
                                        // color={textColor(disabled)}
                                        textAlign={"center"}
                                    >
                                        {researchDomain.name}
                                    </Typography>
                                </Stack>
                            </QueryCard>)
                    }
                </Box>
                {
                    selectedDomains?.length === 0
                        ? <Typography color={colours.warning.warn}  fontSize={"14px"}>Select domain(s) to begin building a query.</Typography>
                        : <></>
                }
            </Stack>
        </Paper>
    )
}

export default ResearchDomainSelector;